import { getOperation } from "../../../../../../../contexts/OperationContext";
import { aDayMillisecond, OP_STATUS, OPERATION_TYPE, operationType } from "../../../../../../../util/varibles/constants";
import { checkLimit } from "../../../../../../../util/varibles/global";
import { IActivityLog, ILevel, IOperation, IVessel } from "../../../../../../../util/varibles/interface";
import { convertPxToTimeByStep } from "../../../../../util";
import { createKeyActivityLog } from "../../actions";
import { LAYOUT_CALENDER } from "../../../../../constants";

interface IGetNewTime {
    get: ReturnType<typeof getOperation>
    activityLogs?: Record<string, IActivityLog[]>
    data: IOperation
    e: MouseEvent
}

const opTypeExclusion = new Set<OPERATION_TYPE | string>([
    OPERATION_TYPE.EVENT,
    OPERATION_TYPE.SERVICE,
    OPERATION_TYPE.SUPPORT
])

export function getNewTime(args: IGetNewTime) {
    const { vesselWidth = 1, vessels, rootKey, currentKey, targetY = 1, loadingKeys, activityLogs: oldaAtivityLogs } = args.get();
    const { clientX, clientY } = args.e;
    const { data, activityLogs = { [rootKey]: oldaAtivityLogs[rootKey] } as Record<string, IActivityLog[]> } = args;

    const layout = document.getElementById(LAYOUT_CALENDER);
    const layoutRect = layout?.getBoundingClientRect();
    const x = clientX - (layoutRect?.left ?? 0);
    const y = clientY - (layoutRect?.top ?? 0);

    const vesselIndex = Math.floor(x / vesselWidth);
    const vessel = vessels[checkLimit(0, vessels.length - 1, vesselIndex)];

    const { activity_log, operation, vessel: vesselOld } = data;
    const { operation_type } = operation;
    const isCheck = !opTypeExclusion.has(operation_type);

    const duration = convertPxToTimeByStep(y - targetY);

    const result = {
        x,
        newStartTime: activity_log[0].est_start_time + duration,
        rootKey,
        currentKey,
        vessel,
        isAction: false,
        error: '',
        vesselIndex,
        isCheck
    }

    if (vessel.id === vesselOld.id && targetY === y) return { ...result, error: '-' };


    if (isCheck) {
        const isError = vessel.operation_type.indexOf(operation_type) === -1;
        if (isError) {
            const { name = '' } = operationType[operation_type] || {};
            return { ...result, error: `${vessel.name} does not support ${name}` }
        }

        const newKey: string = createKeyActivityLog(operation, vessel);
        result.currentKey = newKey;
        if (activityLogs[newKey]) {
            result.rootKey = newKey;
        } else if (!loadingKeys[newKey])
            result.isAction = true;
    }
    return { ...result, vessel }
}

interface IGetNewTimeHorizontal {
    get: ReturnType<typeof getOperation>
    e: MouseEvent
}

interface IGetNewTimeResult {
    y: number;
    newStartTime: number;
    rootKey: string;
    currentKey: string;
    vessel?: IVessel;
    isAction: boolean;
    error: string;
    isCheck: boolean;
    vesselId: string
}

export function getNewTimeHorizontal(args: IGetNewTimeHorizontal) {
    const { vessels, rootKey, currentKey, targetX = 0, loadingKeys, activityLogs, data } = args.get();
    const { clientX, clientY } = args.e;
    let el = document.elementFromPoint(clientX, clientY) as HTMLDivElement;
    const result: IGetNewTimeResult = {
        y: 0,
        newStartTime: 0,
        rootKey,
        currentKey,
        vesselId: data?.vessel.id || "",
        isAction: false,
        error: '',
        isCheck: false
    }
    if (!el) {
        return result;
    }
    const { offsetWidth } = el;
    const timePerPx = aDayMillisecond / offsetWidth;
    const duration = (clientX - targetX) * timePerPx;
    const { operation, vessel: vesselOld } = data || {};
    const { operation_type = OPERATION_TYPE.UNAVAILABLE } = operation || {};
    const isCheck = !opTypeExclusion.has(operation_type);
    result.y = clientY;
    result.newStartTime = (data?.activity_log[0].est_start_time || 0) + duration;
    result.isCheck = isCheck
    result.vessel = vessels.find(item => item.id === el.dataset.vesselId) || data?.vessel;

    if (result.vessel?.id === vesselOld?.id && targetX === clientX) return { ...result, error: '-' };

    if (isCheck && result.vessel && operation) {
        const isError = (result.vessel?.operation_type || []).indexOf(operation_type) === -1;
        if (isError) {
            const { name = '' } = operationType[operation_type] || {};
            return { ...result, error: `${result.vessel.name} does not support ${name}` }
        }

        const newKey: string = createKeyActivityLog(operation, result.vessel);
        result.currentKey = newKey;
        if (activityLogs[newKey]) {
            result.rootKey = newKey;
        } else if (!loadingKeys[newKey])
            result.isAction = true;
    }
    return result
}

export const checkVessel = ({ vessel, operation_type }: { vessel: IVessel, operation_type: OPERATION_TYPE }) => {
    const isError = vessel.operation_type.indexOf(operation_type) === -1;
    if (isError) {
        const { name = '' } = operationType[operation_type] || {};
        return `${vessel.name} does not support ${name}`
    }
    return ''
}

export const findTime = ({ e, activity_log, get }: {
    e: { layerY?: number } & MouseEvent,
    activity_log: IActivityLog[],
    get(): any
}) => {
    const { offsetY, layerY = 0 } = e;
    const { targetY } = get();
    const y = offsetY || layerY;
    const duration = convertPxToTimeByStep(y - targetY);
    return { y, time: activity_log[0].est_start_time + duration };
}

export const findVessel = ({ e, get }: { e: { layerX?: number } & MouseEvent, get(): any }) => {
    const { offsetX, layerX = 0 } = e;
    const x = offsetX || layerX;
    const { vesselWidth, vessels } = get();
    const vesselIndex = Math.floor(x / vesselWidth);
    return { x, vessel: vessels[checkLimit(0, vessels.length - 1, vesselIndex)] };
}

export const checkStatus = (status: any) => [
    OP_STATUS.NEW,
    OP_STATUS.SAVED,
    OP_STATUS.WAITING,
    OP_STATUS.APPROVED,
    OP_STATUS.PENDING
].indexOf(status) !== -1

interface IGetHeightOfOp {
    element: any
}

export function getHeightOfOp(args: IGetHeightOfOp) {
    const { element } = args;
    if (!element)
        return;

    const index = element.children.length - 1;
    const child = element.children[index]
    if (child) {
        const oldHeight = child.style.height;
        child.style.height = 'min-content';
        const { scrollHeight } = child;
        child.style.height = oldHeight
        return scrollHeight + 7;
    }
    return 42;
}

export const calcWidth: { [id: string]: (level: ILevel, width: number, index: number) => any } = {
    true: (level: ILevel, width: number) => {
        return {
            // width: `calc(${(width * .5) + (width * .5 / (level.sub + 1))}% - ${level.sub * 10 + 20}px)`
            width: `calc((${width}% - ${(level.sub + 2) * 10}px)/ ${level.sub + 1})`
        }
    },
    false: (level: ILevel, width: number, index: number) => {
        return {
            // left: `calc(${width * (index + 1) - (width * .5 / (level.sub + 1) * level.level)}% - ${level.level * 10}px)`,
            left: `calc(${width * index}% + ((${width}% - ${(level.sub + 2) * 10}px) * ${level.level} / ${level.sub + 1}) + ${10 * (level.level + 1)}px)`,
            // leftTimeLine: `calc(${width * (index + 1) - (width * .5 / (level.sub + 1) * level.level)}% - ${level.level * 10}px + 110px)`,
            leftTimeLine: `calc(${width * index}% + ((${width}% - ${(level.sub + 2) * 10}px) * ${level.level} / ${level.sub + 1}) + ${10 * (level.level + 1) + 110}px)`,
            // width: `${(width * .5 / (level.sub + 1))}%`
            width: `calc((${width}% - ${(level.sub + 2) * 10}px)/ ${level.sub + 1})`
        }
    }
}
